/**
 * Product guidance email — the middle tier, and why it is NOT consent.
 *
 * Three kinds of email leave Cuelist and they do not share a legal basis:
 *
 *   1. Account and billing — receipts, renewals, invitations, security notices.
 *      Part of running the account. No preference, and none is offered.
 *   2. Product guidance — how to use features in the Cuelist someone already
 *      has. THIS FILE. Sent on legitimate interest with a right to object, so
 *      it is an OPT-OUT and absence means they are receiving it.
 *   3. Marketing and newsletter — see `marketingConsent.js`. Opt-IN, and
 *      absence there means nobody has asked yet.
 *
 * ⚠ TIERS 2 AND 3 MUST NOT SHARE A FIELD. They rest on different bases, so
 * collapsing them forces the strictest rule onto both — which is exactly what
 * the account page did before this existed, and it meant a paying customer who
 * had never been asked about marketing could not be told how to use the feature
 * they were paying for.
 *
 * ⚠ THIS IS NOT A SECOND MARKETING CHANNEL. The moment a guidance email pitches
 * an upgrade, sells a plan, or promotes anything the reader does not already
 * have, it is tier 3 and it needs consent. Nothing in the code can enforce that;
 * it is an editorial line and it is the line the whole tier rests on.
 *
 * The published basis, in `privacy.astro` on the marketing site:
 *
 *   "Sending you information we think you may find useful or which you have
 *    requested from us about our products and services. We may send you
 *    information about the parts of Cuelist you use or that may be relevant to
 *    you. We expect to keep this kind of email to a minimum, and you may object
 *    to being contacted for these reasons..."
 *
 * ⚠ "KEEP THIS KIND OF EMAIL TO A MINIMUM" IS A PUBLISHED COMMITMENT about
 * volume, not a sentiment. It is the same paragraph that authorises the tier.
 *
 * That paragraph also scopes consent to "any marketing or newsletter email"
 * only, which is why guidance does not read `marketing_consent` at all.
 */

export const GUIDANCE_VERSION = 1;

export const GUIDANCE_TEXT =
    'How to use the features you already have. Guides, walkthroughs and the occasional explainer.';

/**
 * The fields written when someone objects, or changes their mind back.
 *
 * ⚠ ABSENCE MEANS RECEIVING, the OPPOSITE of `marketing_consent`, and that is
 * the point rather than an inconsistency. A missing opt-out is not an unanswered
 * question — nothing was ever asked, because objecting is the right being
 * offered. So this needs no backfill and no migration: every existing account is
 * already in the correct state.
 *
 * No copy is stored against the record, unlike consent. Consent has to prove
 * what someone agreed to; an objection only has to record that they made it.
 *
 * @param {boolean} optedOut  true when the person is objecting
 * @param {string}  source    'account_settings' | 'unsubscribe_link'
 */
export function guidanceFields(optedOut, source) {
    return {
        product_guidance_opt_out: !!optedOut,
        product_guidance_opt_out_at: new Date().toISOString(),
        product_guidance_version: GUIDANCE_VERSION,
        product_guidance_source: source
    };
}

/** Is this account receiving guidance email? Absence means yes. */
export function receivesGuidance(user) {
    return !(user && user.product_guidance_opt_out === true);
}

export default {
    GUIDANCE_TEXT,
    GUIDANCE_VERSION,
    guidanceFields,
    receivesGuidance
};


// WEBPACK FOOTER //
// ./src/lib/productGuidance.js